import Section from 'app/frontend/base/components/Section'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useTranslation } from 'react-i18next'

import ProtectedContent from 'app/frontend/base/components/ProtectedContent'
import RosterTable from './components/RosterTable'
import { useMemo } from 'react'
import AddTeamMemberButton from './components/AddTeamMemberButton'
import LeaveTeamButton from 'app/frontend/base/components/LeaveTeamButton'
import RosterSetup from './components/RosterSetup'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { List, LayoutDashboard, TriangleAlert, Info } from 'lucide-react'
import { PREMIUM_BENEFIT_CONFIG } from 'app/frontend/types/custom/PremiumBenefitCategory'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import Card from 'app/frontend/base/components/Card'
import PremiumUpsell from 'app/frontend/base/components/PremiumUpsell'
import usePersistedHashValue from 'app/frontend/base/hooks/usePersistedHashValue'
import { ExcludedTeamRanksHoverCard } from './components/RosterStats/TotalCharacters'
import { cn } from '@/lib/utils'

type TeamRosterView = 'setup' | 'list'

const isTeamRosterView = (value: string): value is TeamRosterView =>
  value === 'setup' || value === 'list'

const TeamRoster = () => {
  const { t } = useTranslation()
  const { team, guild } = useTeam()
  const { value: activeView, setValue: setActiveView } =
    usePersistedHashValue<TeamRosterView>({
      defaultValue: 'setup',
      storageKey: 'team-roster-activeView',
      isValid: isTeamRosterView,
    })

  const handleViewChange = (view: string) => {
    if (isTeamRosterView(view)) {
      setActiveView(view)
    }
  }

  const excludedTeamRanks = useMemo(
    () => team.teamRanks?.filter((r) => !r.roster_visibility) ?? [],
    [team.teamRanks],
  )

  const excludedTeamRankIds = useMemo(
    () => excludedTeamRanks.map((r) => r.id),
    [excludedTeamRanks],
  )

  const filteredMembers = useMemo(
    () =>
      team.members?.filter(
        (m) => !excludedTeamRankIds?.includes(m.teamRank.id),
      ) ?? [],
    [team.members, excludedTeamRankIds],
  )

  const excludedMembers = useMemo(
    () =>
      team.members?.filter((m) =>
        excludedTeamRankIds?.includes(m.teamRank.id),
      ) ?? [],
    [team.members, excludedTeamRankIds],
  )

  const mainMembers = useMemo(
    () => filteredMembers.filter((m) => m.teamRank.player_type === 'main'),
    [filteredMembers],
  )

  const altAndExcludedBreakdown = useMemo(() => {
    const tally = (members: wowaudit.TeamMembership[], fallback: string) =>
      Object.entries(
        members.reduce<Record<string, number>>((acc, m) => {
          const label = m.teamRank.name ?? fallback
          acc[label] = (acc[label] ?? 0) + 1
          return acc
        }, {}),
      ).map(([label, count]) => ({ label, count }))

    return [
      ...tally(
        filteredMembers.filter((m) => m.teamRank.player_type === 'reserve'),
        'Reserve',
      ),
      ...tally(
        filteredMembers.filter((m) => m.teamRank.player_type === 'alt'),
        'Alt',
      ),
      ...tally(excludedMembers, 'Other'),
    ]
  }, [filteredMembers, excludedMembers])

  const altAndExcludedCount = altAndExcludedBreakdown.reduce(
    (sum, { count }) => sum + count,
    0,
  )

  const atLimit =
    guild.characterLimit != null &&
    (team.members?.length ?? 0) >= guild.characterLimit

  const atMaxLimit =
    guild.characterLimit === PREMIUM_BENEFIT_CONFIG.character_limit.max

  return (
    <Section title={`Roster | ${team.name} | ${guild.name}`}>
      <Tabs
        defaultValue='setup'
        value={activeView}
        onValueChange={handleViewChange}
      >
        <h2 className='flex justify-between items-center'>
          <span className='flex items-center gap-3'>
            <span>{t('shared.roster')}</span>
            <Popover>
              <PopoverTrigger asChild>
                <Card
                  className={cn(
                    'rounded-lg px-3 py-0 text-base flex items-center gap-1.5',
                    atLimit &&
                      !atMaxLimit &&
                      'bg-warning border-warning-foreground text-warning-foreground hover:bg-warning/50 cursor-pointer',
                    atLimit &&
                      atMaxLimit &&
                      'bg-info border-info-foreground text-info-foreground hover:bg-info/50 cursor-pointer',
                  )}
                >
                  {atLimit &&
                    (atMaxLimit ? (
                      <Info size={14} />
                    ) : (
                      <TriangleAlert size={14} />
                    ))}
                  {mainMembers.length}
                  {altAndExcludedCount > 0 && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className='text-muted-foreground'>
                          +{altAndExcludedCount}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {altAndExcludedBreakdown
                          .map(({ label, count }) => `${count} ${label}`)
                          .join(', ')}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </Card>
              </PopoverTrigger>
              {atLimit && (
                <PopoverContent
                  className={cn('w-80', atMaxLimit ? 'space-y-3' : 'p-0')}
                >
                  {atMaxLimit ? (
                    <>
                      <p className='font-semibold text-base'>
                        {t('dashboard.team.character-limit-reached')}
                      </p>
                      <p className='text-sm text-muted-foreground'>
                        {t(
                          'dashboard.team.character-limit-reached-description-max',
                          { limit: guild.characterLimit },
                        )}
                      </p>
                    </>
                  ) : (
                    <PremiumUpsell
                      className='border-0'
                      title={t('dashboard.team.character-limit-reached')}
                      description={t(
                        'dashboard.team.character-limit-reached-description',
                        { limit: guild.characterLimit },
                      )}
                      to={`${team.path}/premium`}
                    />
                  )}
                </PopoverContent>
              )}
            </Popover>
          </span>

          <div className='flex items-center gap-3 flex-wrap justify-end'>
            <LeaveTeamButton teamId={team.id} teamPath={team.path} />

            <TabsList className='h-9'>
              <TabsTrigger value='setup'>
                <Tooltip delayDuration={500}>
                  <TooltipTrigger asChild>
                    <LayoutDashboard size={16} />
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('dashboard.team.roster.setup-view')}
                  </TooltipContent>
                </Tooltip>
              </TabsTrigger>
              <TabsTrigger value='list'>
                <Tooltip delayDuration={500}>
                  <TooltipTrigger asChild>
                    <List size={16} />
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('dashboard.team.roster.list-view')}
                  </TooltipContent>
                </Tooltip>
              </TabsTrigger>
            </TabsList>

            <ProtectedContent
              section='roster'
              requirement='admin'
              renderUnauthorized={false}
            >
              <AddTeamMemberButton />
            </ProtectedContent>
          </div>
        </h2>

        <TabsContent value={'list'} id={'list'}>
          <RosterTable members={filteredMembers} />
        </TabsContent>

        <TabsContent value={'setup'} id={'setup'}>
          <RosterSetup members={filteredMembers} />
        </TabsContent>

        {excludedMembers.length > 0 && (
          <>
            <h4 className='mt-8 mb-3 flex items-center gap-2'>
              Other members
              <ExcludedTeamRanksHoverCard
                excludedTeamRanks={excludedTeamRanks}
              />
            </h4>
            <RosterTable members={excludedMembers} />
          </>
        )}
      </Tabs>
    </Section>
  )
}

export default TeamRoster
